// SNWEB-2205 — helpers to keep the livetracker's active tab (and selected
// video) in sync with the URL:
//   /:sport/:league/games/:gamecode/:tab?/:videoId?

import { sanitizeString } from './shared_helpers';

// 'Live Game' -> 'live-game', 'Line combinations' -> 'line-combinations'.
// Delegates to sanitizeString (also used for tab identifiers in TabsDropdown)
// so URL slugs and tab ids cannot drift apart; the extra passes only guard
// against repeated/edge dashes, which sanitizeString does not normalize.
export const slugifyTab = displayName =>
	sanitizeString(String(displayName || '').trim())
		.replace(/-+/g, '-')
		.replace(/(^-|-$)/g, '');

const getPathSegments = pathname =>
	String(pathname || '')
		.split('/')
		.filter(Boolean);

// The game page base: first four segments (/:sport/:league/games/:gamecode),
// regardless of any tab/video suffix.
export const getGameBasePath = pathname =>
	'/' +
	getPathSegments(pathname)
		.slice(0, 4)
		.join('/');

// A page whose trailing URL segments are view state (the open tab), keyed by its page-type
// segment, with how many leading segments identify the page:
//   /:sport/:league/games/:gamecode            + /:tab?/:videoId?
//   /:sport/:league/players/:player_name/:id   + /:tab?
const TABBED_PAGE_BASE_SEGMENTS = { games: 4, players: 5 };

// How many leading segments identify the page, or 0 when the path is not a tabbed page
const getBaseSegmentCount = segments => {
	const baseSegmentCount = TABBED_PAGE_BASE_SEGMENTS[segments[2]];
	return baseSegmentCount && segments.length >= baseSegmentCount ? baseSegmentCount : 0;
};

// The page's own path with any tab segments removed; other paths come back unchanged.
export const getTabbedPageBasePath = pathname => {
	const segments = getPathSegments(pathname);
	const baseSegmentCount = getBaseSegmentCount(segments);
	return baseSegmentCount ? '/' + segments.slice(0, baseSegmentCount).join('/') : pathname;
};

// True when two pathnames are the same tabbed page and only the tab segments differ. Used to
// avoid tearing down ads / re-fetching data / scrolling to top on tab-only URL changes.
export const isSameTabbedPage = (prevPathname, nextPathname) => {
	const prevSegments = getPathSegments(prevPathname);
	const nextSegments = getPathSegments(nextPathname);
	const baseSegmentCount = getBaseSegmentCount(prevSegments);
	return (
		baseSegmentCount > 0 &&
		baseSegmentCount === getBaseSegmentCount(nextSegments) &&
		prevSegments.slice(0, baseSegmentCount).join('/') === nextSegments.slice(0, baseSegmentCount).join('/')
	);
};

export const isGamePagePath = pathname => getPathSegments(pathname)[2] === 'games';

// For consumers that key off the pathname (ad targeting, canonical URLs):
// a game page's tab/video segments are view state, not identity — strip them.
export const stripGameTabSegments = pathname => (isGamePagePath(pathname) ? getGameBasePath(pathname) : pathname);
